import React, { Component } from "react";
import { fetchQuery, graphql, QueryRenderer, type RelayRefetchProp } from "react-relay";

import { faUser } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { compact, debounce, isEqual, noop, omit, reduce, uniqBy } from "lodash";
import type { GraphQLTaggedNode } from "relay-runtime";
import {
	Dropdown,
	type DropdownItemProps,
	type DropdownOnSearchChangeData,
	type DropdownProps,
	Icon,
	Input,
} from "semantic-ui-react";

import { smart } from "~/lib/relayUtil";
import { environment } from "~/models";
import type { Omit } from "~/types";
import Avatar from "~/ui/legacy/Avatar";

import type { UserSelect_user$data as DirtyUser } from "~/__generated__/UserSelect_user.graphql";
import type { UserSelect_viewer$data as Viewer } from "~/__generated__/UserSelect_viewer.graphql";
import type { UserSelectExistingUsersQuery } from "~/__generated__/UserSelectExistingUsersQuery.graphql";
import type { UserSelectRenderer_Query as QueryType } from "~/__generated__/UserSelectRenderer_Query.graphql";

import "./css/UserSelect.css";

graphql`
	fragment UserSelect_user on User {
		id
		fullName
		firstName
		lastName
		email
		image
		relationshipToViewer
		isGuest

		internalAttributes {
			manager {
				id
				fullName
				firstName
				lastName
				email
				image
				isGuest
				relationshipToViewer
			}
		}
	}
`;

graphql`
	fragment UserSelect_contact on ContactNode {
		id
		email
		fullName
		image
		user {
			...UserSelect_user @relay(mask: false)
		}
	}
`;

export type User = Omit<DirtyUser, " $fragmentType">;

export interface DropdownOption {
	id: string;
	key: string;
	value: string;
	text: string;
	user: User | null;
	title?: string;
	className?: string;
	image?: {
		avatar: boolean;
		src: string;
	};
	content?: React.ReactNode;
}

interface DropdownOptions {
	[key: string]: DropdownOption;
	[key: number]: DropdownOption;
}

interface DropdownRef extends React.Component<DropdownProps, { open: boolean; selectedIndex: number }> {
	searchRef: { focus: () => void };
}

interface UserSelectRendererProps {
	reportsTo?: { id: string } | null;
	reportsUnder?: { id: string } | null;
	doesNotReportUnder?: { id: string } | null;
	idIn?: string[];
	className?: string;
	includedSelections?: DropdownOption[];
	hideIncludedSelectionsOnSearch?: boolean;
	focusOnMount?: boolean;
	openOnMount?: boolean;
	search?: boolean;
	selection?: boolean;
	multiple?: boolean;
	singleAvatar?: boolean;
	iconOnly?: boolean;
	textOnly?: boolean;
	text?: string;
	value?: string | string[];
	nullable?: boolean;
	placeholder?: string;
	hasPlaceholderIcon?: boolean;
	exclude?: string[];
	locked?: string[];
	disabled?: boolean;
	options?: DropdownOptions;
	ranked?: boolean;
	promotedUserIds?: string[];
	filterGuests?: boolean;
	filterManagers?: boolean;
	onChange?: (value: DropdownOption | DropdownOption[] | null) => void;
	defaultIcon?: React.ReactNode;
	forceDownward?: boolean;
}

type UserSelectProps = UserSelectRendererProps & {
	readonly viewer: Viewer;
	relay?: RelayRefetchProp;
};

interface UserSelectState {
	query: string;
	fetching: boolean;
	open: boolean;
	selected: DropdownOption | DropdownOption[] | null;
}

const fetchExistingUsersQuery = graphql`
	query UserSelectExistingUsersQuery($idIn: [ID]!) {
		viewer {
			users(idIn: $idIn) {
				edges {
					node {
						...UserSelect_user @relay(mask: false)
					}
				}
			}
		}
	}
`;

class UserSelectComponent extends Component<UserSelectProps, UserSelectState> {
	static defaultProps = {
		includedSelections: [],
		focusOnMount: false,
		openOnMount: false,
		search: true,
		selection: true,
		reportsTo: null,
		reportsUnder: null,
		multiple: false,
		singleAvatar: true,
		iconOnly: false,
		textOnly: false,
		forceDownward: false,
	};

	static Fragments = {
		viewer: graphql`
			fragment UserSelect_viewer on Viewer
			@argumentDefinitions(
				search: { type: "String" }
				reportsTo: { type: "ID" }
				reportsUnder: { type: "ID" }
				doesNotReportUnder: { type: "ID" }
				ranked: { type: "Boolean" }
				isGuests: { type: "Boolean" }
				isManager: { type: "Boolean" }
				idIn: { type: "[ID]", defaultValue: null }
			) {
				user {
					id
				}
				selectUsers: users(
					first: 25
					search: $search
					reportsTo: $reportsTo
					reportsUnder: $reportsUnder
					doesNotReportUnder: $doesNotReportUnder
					idIn: $idIn
					ranked: $ranked
					isGuests: $isGuests
					isManager: $isManager
				) {
					edges {
						node {
							...UserSelect_user @relay(mask: false)
						}
					}
				}
			}
		`,
	};

	static Refetch = graphql`
		query UserSelectRefetchQuery(
			$search: String
			$ranked: Boolean
			$isGuests: Boolean
			$isManager: Boolean
			$reportsTo: ID
			$reportsUnder: ID
			$doesNotReportUnder: ID
			$idIn: [ID]
		) {
			viewer {
				...UserSelect_viewer
					@arguments(
						search: $search
						ranked: $ranked
						isGuests: $isGuests
						isManager: $isManager
						reportsTo: $reportsTo
						reportsUnder: $reportsUnder
						doesNotReportUnder: $doesNotReportUnder
						idIn: $idIn
					)
			}
		}
	`;

	static prepareVars = () => ({
		search: null,
		reportsTo: null,
		reportsUnder: null,
		idIn: null,
	});

	options: DropdownOption[] = [];
	dropdownRef: DropdownRef | null = null;
	refetch = noop;
	debouncedRefetch = debounce(this.refetch, 200);

	static userToOption = (user: User): DropdownOption => {
		return {
			user,
			id: user.id,
			key: user.id,
			value: user.id,
			text: user.fullName,
			image: {
				avatar: true,
				src: user.image,
			},
		};
	};

	constructor(props: UserSelectProps) {
		super(props);

		this.state = {
			query: "",
			fetching: false,
			open: false,
			selected: props.multiple ? [] : null,
		};

		this.refetch = (search: string) => {
			const { relay, reportsTo, reportsUnder, idIn, exclude, ranked } = this.props;
			if (relay) {
				relay.refetch(
					{
						search,
						ranked,
						idIn,
						exclude,
						reportsTo: reportsTo ? reportsTo.id : null,
						reportsUnder: reportsUnder ? reportsUnder.id : null,
					},
					null,
					() => {
						this.setState({ fetching: false });
					},
				);
			}
		};

		this.debouncedRefetch = debounce(this.refetch, 200);
	}

	componentDidMount() {
		if (this.props.focusOnMount) {
			if (this.dropdownRef && this.dropdownRef.searchRef) this.dropdownRef.searchRef.focus();
			if (this.props.openOnMount) this.setState({ open: true });
		}

		if (this.props.value) {
			this.setSelected(this.props.value);
			this.loadSelectedOptions();
		}
	}

	componentDidUpdate(prevProps: UserSelectProps) {
		if (!isEqual(this.props.value, prevProps.value)) this.setSelected(this.props.value);
	}

	loadSelectedOptions() {
		let values = typeof this.props.value === "string" ? [this.props.value] : this.props.value;

		if (!values) return;

		// Filter out not starting from encoded "User" prefix
		values = values.filter(val => val.startsWith("VXNlc"));

		if (!values || values.length === 0) return;

		fetchQuery<UserSelectExistingUsersQuery>(environment, fetchExistingUsersQuery, { idIn: values })
			.toPromise()
			.then(response => {
				const users = response?.viewer.users;
				if (!users) return;

				this.setState(prev => {
					const selected = users.edges.map(edge => UserSelectComponent.userToOption(edge.node));
					const currentSelected = prev.selected ? (Array.isArray(prev.selected) ? prev.selected : [prev.selected]) : [];
					const newSelected = selected.filter(item => !currentSelected.find(opt => opt.id === item.id));
					return { ...prev, selected: [...currentSelected, ...newSelected] };
				});
			});
	}

	private sortAndToArray(options: DropdownOptions): DropdownOption[] {
		const values = [...Object.values(options)];
		const { promotedUserIds } = this.props;
		if (!promotedUserIds || promotedUserIds.length === 0) {
			values.sort((a, b) => a.text.localeCompare(b.text));
			return values;
		}

		values.sort((a, b) => {
			const indexA = promotedUserIds.indexOf(a.id);
			const indexB = promotedUserIds.indexOf(b.id);
			if (indexA === -1 && indexB === -1) {
				return 0;
			}
			if (indexA === -1 && indexB > -1) {
				return 1;
			}
			if (indexA > -1 && indexB === -1) {
				return -1;
			}
			return indexA - indexB;
		});

		return values;
	}

	getOptions(): DropdownOptions {
		const setOptionsImages = (options: DropdownOptions): DropdownOptions => {
			return reduce(
				options,
				(acc, option, key) => {
					const { user } = option;
					if (!user) return { ...acc, [key]: option };

					const newOption = {
						...option,
						content: (
							<React.Fragment>
								{/* The className `image` is here to make Semantic's image styles apply to guests' avatars,
								 * which have a wrapper `span` around them and are otherwise misaligned. */}
								<Avatar className="image" user={user} link={false} />
								{user.fullName}
							</React.Fragment>
						),
					};
					delete newOption.image;

					return { ...acc, [key]: newOption };
				},
				{},
			);
		};

		let options: DropdownOptions = {};
		if (this.props.options) {
			options = reduce(
				Object.keys(this.props.options),
				(acc: DropdownOptions, key: string) => {
					const item = this.props.options && this.props.options[key];
					if (!item || !item.text) return acc;

					if (!item.text.toLowerCase().includes(this.state.query.toLowerCase())) return acc;

					return { ...acc, [key]: item };
				},
				{},
			);
			return setOptionsImages(options);
		}

		if (this.props.multiple && this.state.selected) {
			for (const opt of this.state.selected as DropdownOption[]) {
				options[opt.key] = opt;
			}
		} else if (this.props.nullable) {
			options[0] = {
				id: "",
				key: "",
				value: "",
				text: this.props.placeholder || "Select a User",
				user: null,
			};
		}

		const { selectUsers } = this.props.viewer;

		if (selectUsers && selectUsers.edges) {
			for (const edge of selectUsers.edges) {
				if (edge && edge.node) {
					options[edge.node.id] = {
						id: edge.node.id,
						key: edge.node.id,
						value: edge.node.id,
						text: edge.node.fullName,
						title: edge.node.fullName,
						user: edge.node,
						image: {
							avatar: true,
							src: edge.node.image,
						},
					};
				}
			}
		}

		if (
			this.props.includedSelections &&
			this.props.includedSelections.length &&
			(!this.props.hideIncludedSelectionsOnSearch || !this.state.query)
		) {
			for (const selection of this.props.includedSelections) {
				options[selection.value] = selection;
			}
		}

		if (this.props.exclude) options = omit(options, this.props.exclude);

		return setOptionsImages(options);
	}

	setSelected = (value: string | number | boolean | (string | number | boolean)[] | undefined) => {
		const options = this.getOptions();

		let selected = null;
		if (this.props.multiple && value) {
			const locked = this.props.locked ? this.props.locked.map(key => options[key]) : [];
			selected = (value as (string | number)[]).map(key => options[key]).filter(i => !!i);
			selected = uniqBy([...compact(locked), ...selected], "id");
		} else {
			selected = options[value as string | number];
		}

		this.setState({ selected });
		return selected;
	};

	onChange = (_e: React.SyntheticEvent<HTMLElement> | null, data: DropdownProps) => {
		const selected = this.setSelected(data.value);

		this.setState({
			selected,
			query: "",
		});

		if (this.props.onChange) this.props.onChange(selected);
	};

	onSearchChange = (_e: React.SyntheticEvent<HTMLElement>, { searchQuery }: DropdownOnSearchChangeData) => {
		this.debouncedRefetch.cancel();

		this.setState({
			query: searchQuery,
			fetching: true,
		});

		this.debouncedRefetch(searchQuery);

		return true;
	};

	onKeyDown = (e: React.KeyboardEvent) => {
		if (
			e.key === "Tab" &&
			this.props.multiple &&
			this.state.selected &&
			this.dropdownRef &&
			this.dropdownRef.state.open
		) {
			e.preventDefault();

			const newOptions = this.options.filter(item => {
				return (this.state.selected as DropdownOption[]).find(opt => item.id === opt.id);
			});
			if (!newOptions) return;

			const selectedIndex = this.dropdownRef ? this.dropdownRef.state.selectedIndex || 0 : 0;
			const newSelected = newOptions[selectedIndex];
			if (!newSelected) return;

			const selected = uniqBy([...(this.state.selected as DropdownOption[]), newSelected], "id");
			this.setState({ selected, query: "" });

			if (this.props.onChange) this.props.onChange(selected);
			const selectedIds = selected.map(item => item.id);

			const furtherOptions = newOptions.filter(item => {
				!selectedIds.includes(item.id);
			});

			if (furtherOptions.length == 0) {
				this.setState({ open: false });
			}
		}
	};

	getIcon = (selected: DropdownOption | DropdownOption[] | null) => {
		if (this.props.multiple || !this.props.singleAvatar || !selected) {
			if (this.props.hasPlaceholderIcon) {
				return (
					<React.Fragment>
						<div className="placeholder-icon">
							<FontAwesomeIcon style={{ fontSize: "1em" }} icon={faUser} />
						</div>{" "}
						<Icon name="angle down" />
					</React.Fragment>
				);
			} else {
				return "angle down";
			}
		}

		const { user } = selected as DropdownOption;
		const icon = user ? <Avatar link={false} className="additional icon" user={user} /> : this.props.defaultIcon;

		if (!icon) return "angle down";
		return (
			<React.Fragment>
				{icon} <Icon name="angle down" />
			</React.Fragment>
		);
	};

	renderLabel = (item: DropdownItemProps) => {
		const output: {
			className: string;
			color: string;
			content?: DropdownItemProps["text"];
			image?: DropdownItemProps["image"];
			removeIcon?: string;
		} = {
			className: "user-select-item",
			color: "blue",
			removeIcon: "delete",
		};

		if (!this.props.iconOnly) output.content = item.text;
		if (!this.props.textOnly && (item.image || item.user.image)) {
			output.className = `${output.className} image`;
			output.image = item.image ? item.image : <Avatar className="image" user={item.user} link={false} />;
		}
		if (this.props.locked && this.props.locked.includes(item.key)) {
			output.className = `${output.className} locked`;
		}

		return output;
	};

	onOpen = () => this.setState({ open: true });

	onClose = () => this.setState({ open: false });

	handleDropdownRef = (ref: DropdownRef) => {
		this.dropdownRef = ref;
	};

	dropdownSearch = (options: DropdownItemProps[]) => {
		// kind of hack... search=false breaks input. But we already have search in query, so just return results
		return options;
	};

	onMouseDown = (e: React.MouseEvent) => {
		if (e.target instanceof Element && !document.activeElement?.contains(e.target)) {
			e.preventDefault();
		}
	};

	render() {
		let options: DropdownOption[] = [];
		this.options = options = this.sortAndToArray(this.getOptions());

		let value: string | string[] | undefined = undefined;
		const { selected } = this.state;
		if (selected) {
			value = this.props.multiple ? (selected as DropdownOption[]).map(opt => opt.id) : (selected as DropdownOption).id;
		}

		return (
			<Dropdown
				basic
				fluid
				deburr
				selectOnNavigation
				selectOnBlur
				closeOnChange
				closeOnBlur
				className={cx("user-select", this.props.className)}
				text={this.props.text}
				icon={this.getIcon(selected)}
				open={this.state.open && !this.state.fetching}
				onOpen={this.onOpen}
				onClose={this.onClose}
				openOnFocus={this.props.multiple && value instanceof Array ? value.length > 0 : !value}
				ref={this.handleDropdownRef}
				searchQuery={this.state.query}
				search={this.dropdownSearch}
				onKeyDown={this.onKeyDown}
				minCharacters={0} // HACK
				loading={this.state.fetching}
				onSearchChange={this.onSearchChange}
				renderLabel={this.renderLabel}
				value={value}
				disabled={this.props.disabled}
				onChange={this.onChange}
				onMouseDown={this.onMouseDown}
				options={options}
				{...(this.props.forceDownward ? { upward: false } : {})}
				{...omit(this.props, [
					"onChange",
					"focusOnMount",
					"openOnMount",
					"includedSelections",
					"ranked",
					"reportsTo",
					"reportsUnder",
					"idIn",
					"iconOnly",
					"textOnly",
					"singleAvatar",
					"nullable",
					"options",
					"className",
					"value",
					"disabled",
					"search",
					"defaultIcon",
					"forceDownward",
					"doesNotReportUnder",
					"filterGuests",
					"promotedUserIds",
				])}
			/>
		);
	}
}

const UserSelect = smart(UserSelectComponent);

class UserSelectRenderer extends Component<UserSelectRendererProps> {
	static userToOption = UserSelectComponent.userToOption;

	query: GraphQLTaggedNode = graphql`
		query UserSelectRenderer_Query(
			$search: String
			$ranked: Boolean
			$isGuests: Boolean
			$isManager: Boolean
			$reportsTo: ID
			$reportsUnder: ID
			$doesNotReportUnder: ID
			$idIn: [ID]
		) {
			viewer {
				...UserSelect_viewer
					@arguments(
						search: $search
						ranked: $ranked
						isGuests: $isGuests
						isManager: $isManager
						reportsTo: $reportsTo
						reportsUnder: $reportsUnder
						doesNotReportUnder: $doesNotReportUnder
						idIn: $idIn
					)
			}
		}
	`;

	render() {
		return (
			<QueryRenderer<QueryType>
				query={this.query}
				environment={environment}
				variables={{
					search: "",
					ranked: this.props.ranked,
					reportsTo: this.props.reportsTo ? this.props.reportsTo.id : null,
					reportsUnder: this.props.reportsUnder ? this.props.reportsUnder.id : null,
					doesNotReportUnder: this.props.doesNotReportUnder ? this.props.doesNotReportUnder.id : null,
					idIn: this.props.idIn,
					isManager: !!this.props.filterManagers,
					...(this.props.filterGuests ? { isGuests: false } : {}),
				}}
				// eslint-disable-next-line react/jsx-no-bind
				render={({ error: _error, props }: { error: Error | null; props: QueryType["response"] | null }) => {
					if (!props) {
						return (
							<Input loading disabled iconPosition="left" placeholder="Loading..." className={this.props.className} />
						);
					}

					return <UserSelect {...props} {...this.props} />;
				}}
			/>
		);
	}
}

// WORKAROUND: STORYBOOK (https://github.com/styleguidist/react-docgen-typescript/issues/394)
/**
 * A UserSelect dropdown can be used to select a single or multiple users.
 * @deprecated Use the new {@link UserSelector} or {@link UserMultiSelector} base class instead.
 */
export default UserSelectRenderer;
